import {
  ForwardedRef,
  forwardRef,
  memo,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react';
import cl from 'classnames';

import { ClassName, FileUrl } from '../../../../../types';
import { IconsEnum } from '../../../../../assets/icons/types';
import { ModelViewerEditorRefType } from './ModelViewerEditor.types';

import { useModelViewerEditor } from './hooks/useModelViewerEditor';
import { useModelViewerLifestyleGeneration } from './hooks/useModelViewerLifestyleGeneration';
import { useModelViewerBackgroundControls } from './hooks/useModelViewerBackgroundControls';
import { useModelViewerToolbarButtonClass } from './hooks/useModelViewerToolbarButtonClass';
import { getVymirConfigForNanoId } from './vymirConfiguratorMap';
import { useVymirConfigurator } from './hooks/useVymirConfigurator';
import { useModelViewerRenderGeneration } from './hooks/useModelViewerRenderGeneration';
import { useModelViewerIncomingPayload } from './hooks/useModelViewerIncomingPayload';
import {
  MODEL_VIEWER_ASPECT_RATIO_AUTO,
  modelViewerAspectRatioValues
} from './modelViewerAspectRatioConstants';
import { resolveModelViewerAspectRatio } from './utils/resolveModelViewerAspectRatio';
import { ModelViewerConfiguratorOverlay } from './components/ModelViewerConfiguratorOverlay';

import { ModelViewerEditorDropZone } from './components/ModelViewerEditorDropZone';
import { ModelViewerLifestylePromptCard } from './components/ModelViewerLifestylePromptCard';
import { ModelViewerLifestyleImageOverlay } from './components/ModelViewerLifestyleImageOverlay';
import { ModelViewerLifestyleSidebar } from './components/ModelViewerLifestyleSidebar';
import { ModelViewerBackgroundButton } from './components/ModelViewerBackgroundButton';
import { ModelViewerAspectRatioFrame } from './components/ModelViewerAspectRatioFrame';
import { ModelViewerDisplaySettingsButton } from './components/ModelViewerDisplaySettingsButton';
import { ModelViewerColorPickerPopup } from './components/ModelViewerColorPickerPopup';
import { ModelViewerImageBackgroundModal } from './components/ModelViewerImageBackgroundModal';
import { DownloadModelButton } from '../../buttons/DownloadModelButton.tsx';
import { ModelViewerHelperTooltipButton } from '../../buttons/ModelViewerHelperTooltipButton';
import { PureTooltipIconButtonHelper } from '../../../../../helpers/buttons/PureTooltipIconButtonHelper';
import { StaticImageHelper } from '../../../../../helpers/StaticImageHelper';
import {
  ModelViewer,
  ModelViewerProps,
  ModelViewerScreenshotHandle
} from '../../../../../helpers/ModelViewer';
import { PureIconButtonHelper } from '../../../../../helpers/buttons/PureIconButtonHelper';
import { CheckPermissions } from '../../../../../helpers/CheckPermissions';
import { Trans } from 'react-i18next';

import { Translate } from '../../../../../helpers/Translate';

import { modelViewersKeys, words } from '../../../../../locales/keys';
import { LifestyleGenerationMode } from '../../../../lifestyleGenerations/lifestyleGenerationsTypes';
import { RenderGenerationStatus } from '../../../../renderGenerations/renderGenerationsTypes';
import { TooltipPlacement } from '../../../../../helpers/tooltips/tooltipsConstants';
import { useCurrentUser } from '../../../../../auth/hooks/useAuth';
import { ModelViewerPermissions } from '../../../modelViewerConstants';
import { downloadFile } from '../../../../../utils/downloadFile';
import { buildDownloadName } from './utils/buildDownloadName';

type ModelViewerEditorProps = Pick<
  ModelViewerProps,
  'cameraControls' | 'dataGa'
> & {
  className?: ClassName;
  title?: string;
  withDownloadButton?: boolean;
  productNanoId?: string | null;
  model?: {
    file: FileUrl;
    name: string;
  };
  initialModelIndex?: number;
  models?: {
    file: FileUrl;
    name: string;
  }[];
};

function ModelViewerEditor(
  {
    className,
    dataGa,
    model,
    initialModelIndex,
    models,
    cameraControls,
    title,
    withDownloadButton,
    productNanoId
  }: ModelViewerEditorProps,
  ref: ForwardedRef<ModelViewerEditorRefType>
) {
  const screenshotRef = useRef<ModelViewerScreenshotHandle>(null);
  const [isAiModeActive, setIsAiModeActive] = useState(false);

  const modelViewerEditorData = useModelViewerEditor({
    model,
    initialModelIndex,
    models
  });

  // model_id makes the backend render the product server-side, so it must
  // only be sent while the viewer still shows that product's own model.
  const generationModelId = modelViewerEditorData.isModelReplaced
    ? null
    : productNanoId;

  const { buildIncomingPayload } = useModelViewerIncomingPayload({
    modelViewerEl: modelViewerEditorData.modelViewerEl
  });

  const {
    generations,
    activeViewId,
    setActiveViewId,
    prompt,
    setPrompt,
    referenceFiles,
    isUploadingReference,
    isGenerating,
    generateError,
    handleReferenceFilesChange,
    handleReferenceFileDrop,
    handleRemoveReference,
    handleGenerate,
    handleEditGenerate,
    handleAdjust,
    handleRemoveGeneration,
    handleStopGeneration,
    selectedCommand,
    setSelectedCommand,
    clearSelectedCommand,
    pendingEditRestore,
    clearPendingEditRestore
  } = useModelViewerLifestyleGeneration({
    modelViewerEl: modelViewerEditorData.modelViewerEl,
    screenshotRef,
    backgroundMode: modelViewerEditorData.backgroundMode,
    backgroundCssValue: modelViewerEditorData.backgroundCssValue,
    aspectRatio: modelViewerEditorData.aspectRatio,
    imageSize: modelViewerEditorData.imageSize,
    modelId: generationModelId,
    buildIncomingPayload
  });

  const hideBackground =
    isAiModeActive && selectedCommand !== LifestyleGenerationMode.Silo;

  const currentUser = useCurrentUser();
  const hasImageBackground = currentUser?.hasPermissions(
    ModelViewerPermissions.READ_MODEL_VIEWER_IMAGE_BACKGROUND
  );
  const hasLifestyleCommand = currentUser?.hasPermissions(
    ModelViewerPermissions.READ_MODEL_VIEWER_LIFESTYLE_COMMAND
  );
  const hasSiloCommand = currentUser?.hasPermissions(
    ModelViewerPermissions.READ_MODEL_VIEWER_SILO_COMMAND
  );
  const hasCloseUpCommand = currentUser?.hasPermissions(
    ModelViewerPermissions.READ_MODEL_VIEWER_CLOSE_UP_COMMAND
  );
  const hasStudioLifestyleCommand = currentUser?.hasPermissions(
    ModelViewerPermissions.READ_MODEL_VIEWER_STUDIO_LIFESTYLE_COMMAND
  );

  const allowedCommands = useMemo(() => {
    const modes: LifestyleGenerationMode[] = [];
    if (hasLifestyleCommand) modes.push(LifestyleGenerationMode.Lifestyle);
    if (hasSiloCommand) modes.push(LifestyleGenerationMode.Silo);
    if (hasCloseUpCommand) modes.push(LifestyleGenerationMode.CloseUp);
    if (generationModelId && hasStudioLifestyleCommand) {
      modes.push(LifestyleGenerationMode.ProductLifestyle);
    }
    return modes;
  }, [
    hasLifestyleCommand,
    hasSiloCommand,
    hasCloseUpCommand,
    hasStudioLifestyleCommand,
    generationModelId
  ]);

  const {
    handlePrevItem,
    handleNextItem,
    activeItem,
    setModelViewerEl,
    multiplyItems,
    withDimensions,
    toggleWithDimensions,
    withRuler,
    setWithRuler,
    units,
    setUnits,
    withGridAxesHelper,
    toggleWithGridAxesHelper,
    withAvailableAnimations,
    toggleAnimationState,
    isPaused,
    isDragging,
    fileInput,
    dropZoneRootEl,
    dropError,
    handleDropClick,
    handleDropInputChange,
    handleDrop,
    handleDragEnter,
    handleDragOver,
    handleDragLeave,
    isNeutralMode,
    toggleNeutralMaterial,
    meshEdgesMode,
    setMeshEdgesMode,
    isModelLoaded,
    aspectRatio,
    setAspectRatio,
    imageSize,
    setImageSize,
    getAspectRatioCssValue,
    showGridLines,
    toggleGridLines,
    backgroundMode,
    customColor,
    backgroundImageUrl,
    backgroundCssValue,
    sessionImageUrls,
    setPresetBackground,
    setCustomColor,
    setBackgroundImage,
    addSessionImageUrl,
    removeSessionImageUrl,
    setCurrentModelExternal
  } = modelViewerEditorData;

  // Only auto needs a crop: in every other mode the viewer box is already
  // CSS-constrained to the preset, so the capture is left byte-identical.
  const getScreenshotCropRatio = useCallback(() => {
    if (aspectRatio !== MODEL_VIEWER_ASPECT_RATIO_AUTO) return undefined;
    return modelViewerAspectRatioValues[
      resolveModelViewerAspectRatio(
        aspectRatio,
        modelViewerEditorData.modelViewerEl?.getBoundingClientRect()
      )
    ];
  }, [aspectRatio, modelViewerEditorData.modelViewerEl]);

  const vymirConfig = productNanoId
    ? getVymirConfigForNanoId(productNanoId)
    : null;

  const vymir = useVymirConfigurator({
    config: vymirConfig,
    onModelExported: setCurrentModelExternal
  });

  const {
    renderGenerations,
    isRequesting: isRequestingRender,
    handleRequestRender,
    handleRemoveRenderGeneration,
    handleStopRenderGeneration
  } = useModelViewerRenderGeneration({
    modelViewerEl: modelViewerEditorData.modelViewerEl,
    modelId: generationModelId,
    setActiveViewId,
    setIsAiModeActive,
    aspectRatio: modelViewerEditorData.aspectRatio,
    imageSize: modelViewerEditorData.imageSize
  });

  const mergedGenerations = useMemo(
    () =>
      [...generations, ...renderGenerations].sort(
        (a, b) => b.createdOrder - a.createdOrder
      ),
    [generations, renderGenerations]
  );

  useImperativeHandle(ref, () => ({
    ...modelViewerEditorData,
    generationsCount: mergedGenerations.length
  }));

  const activeGeneration =
    activeViewId !== 'model'
      ? mergedGenerations.find((g) => g.localId === activeViewId) ?? null
      : null;

  const isActiveGenerationLoading =
    activeGeneration?.kind === 'render'
      ? activeGeneration.status === RenderGenerationStatus.Pending ||
        activeGeneration.status === RenderGenerationStatus.Processing
      : activeGeneration?.status === 'pending' ||
        activeGeneration?.status === 'processing' ||
        activeGeneration?.status === 'preparing_source';

  const {
    colorPickerOpen,
    colorPickerPrevColor,
    imageModalOpen,
    handleOpenColorPicker,
    handleConfirmColorPicker,
    handleCancelColorPicker,
    handleOpenImageModal,
    handleCloseImageModal
  } = useModelViewerBackgroundControls({
    backgroundMode,
    customColor,
    setCustomColor,
    setPresetBackground
  });

  const getButtonClass = useModelViewerToolbarButtonClass(backgroundMode);

  const toolButtonBaseClass =
    'py-2 pl-2 pr-2 rounded-md inline-flex items-center whitespace-nowrap text-sm font-medium leading-6 focus:ring-base focus:ring-offset-0';
  const getToolButtonClass = (isActive: boolean) =>
    isActive
      ? cl(
          toolButtonBaseClass,
          'text-blue-600 hover:text-blue-700 bg-blue-500/20 hover:bg-blue-500/30 dark:text-blue-400 dark:hover:text-blue-300'
        )
      : getButtonClass(false);

  const toolbarDivider = (
    <div className="w-px h-5 bg-gray-200 dark:bg-gray-700 self-center mx-0.5" />
  );

  return (
    <div
      className="flex h-full overflow-hidden"
      ref={dropZoneRootEl}
      onDragEnter={imageModalOpen ? undefined : handleDragEnter}
      onDragLeave={imageModalOpen ? undefined : handleDragLeave}
    >
      {/* 3D viewer column */}
      <div className={cl('relative flex-1 overflow-hidden', className)}>
        {vymir.isOpen && (
          <ModelViewerConfiguratorOverlay
            iframeRef={vymir.iframeRef}
            embedUrl={vymir.embedUrl}
            isReady={vymir.isReady}
          />
        )}

        <ModelViewerAspectRatioFrame
          cssValue={getAspectRatioCssValue()}
          showGridLines={showGridLines}
        >
          <ModelViewer
            src={activeItem?.file}
            cameraControls={cameraControls}
            className="w-full h-full"
            ref={setModelViewerEl}
            withDimensions={withDimensions}
            withRuler={withRuler}
            units={units}
            withGridAxesHelper={withGridAxesHelper}
            autoPlay
            dataGa={dataGa}
            meshEdgesMode={meshEdgesMode}
            screenshotRef={screenshotRef}
            background={
              hideBackground ? undefined : backgroundCssValue || undefined
            }
          />
        </ModelViewerAspectRatioFrame>

        <ModelViewerEditorDropZone
          activeItem={activeItem}
          isDragging={isDragging}
          fileInput={fileInput}
          dropError={dropError}
          handleDropClick={handleDropClick}
          handleDropInputChange={handleDropInputChange}
          handleDrop={handleDrop}
          handleDragOver={handleDragOver}
        />

        {/* Fullscreen generated image overlay */}
        <CheckPermissions
          actions={[
            ModelViewerPermissions.READ_MODEL_VIEWER_LIFESTYLE_GENERATOR,
            ModelViewerPermissions.READ_MODEL_VIEWER_RENDER_GENERATOR
          ]}
        >
          {activeGeneration?.kind === 'lifestyle' &&
            activeGeneration.resultFullUrl && (
              <ModelViewerLifestyleImageOverlay
                url={activeGeneration.resultFullUrl}
                modelName={title ?? activeItem?.name}
                mode={activeGeneration.mode}
                generation={activeGeneration}
                isGenerating={isGenerating}
                generateError={generateError}
                pendingEditRestore={pendingEditRestore}
                onBack={() => setActiveViewId('model')}
                onEditGenerate={handleEditGenerate}
                onClearPendingEditRestore={clearPendingEditRestore}
              />
            )}
          {activeGeneration?.kind === 'render' &&
            activeGeneration.resultFullUrl && (
              <div className="absolute inset-0 z-30 bg-white dark:bg-gray-900 flex items-center justify-center">
                <StaticImageHelper
                  src={activeGeneration.resultFullUrl}
                  alt=""
                  className="w-full h-full object-contain"
                />

                <div className="absolute bottom-4 left-4 flex items-center gap-2 z-40">
                  <PureIconButtonHelper
                    icon={IconsEnum.CHEVRON_LEFT}
                    iconClassName="h-5 w-5"
                    i18nText={modelViewersKeys.backToModel}
                    className="py-1.5 pl-1.5 pr-3 space-x-1 rounded-md inline-flex items-center whitespace-nowrap text-sm font-medium leading-5 bg-gray-200 hover:bg-gray-300 text-gray-800 dark:text-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 transition-colors"
                    onClick={() => setActiveViewId('model')}
                  />
                  <PureIconButtonHelper
                    icon={IconsEnum.DOWNLOAD_SOLID}
                    iconClassName="h-5 w-5"
                    i18nText={words.download}
                    className="py-1.5 pl-1.5 pr-3 space-x-1 rounded-md inline-flex items-center whitespace-nowrap text-sm font-medium leading-5 bg-gray-200 hover:bg-gray-300 text-gray-800 dark:text-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 transition-colors"
                    onClick={() => {
                      if (!activeGeneration.resultFullUrl) return;
                      downloadFile({
                        downloadFile: {
                          file: activeGeneration.resultFullUrl,
                          name: buildDownloadName(
                            title ?? activeItem?.name,
                            'silo-render',
                            activeGeneration.resultFullUrl
                          )
                        },
                        withBlob: true
                      });
                    }}
                  />
                </div>
              </div>
            )}
          {activeGeneration &&
            isActiveGenerationLoading &&
            (activeGeneration.kind === 'render' ? (
              <div className="absolute inset-0 z-30 bg-white dark:bg-gray-900 flex items-center justify-center flex-col gap-4">
                <div className="animate-spin w-16 h-16 rounded-full border-4 border-gray-200 dark:border-gray-700 border-t-blue-600 dark:border-t-blue-600" />
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  <Translate id={modelViewersKeys.renderingImage} />
                </p>
                <div className="max-w-sm text-left text-xs leading-relaxed text-gray-400 dark:text-gray-500 space-y-2">
                  <p>
                    <Trans
                      i18nKey={modelViewersKeys.renderingImageDescription1}
                      components={{
                        accent: (
                          <span className="font-medium text-gray-500 dark:text-gray-400" />
                        )
                      }}
                    />
                  </p>
                  <p>
                    <Translate
                      id={modelViewersKeys.renderingImageDescription2}
                    />
                  </p>
                  <p>
                    <Translate
                      id={modelViewersKeys.renderingImageDescription3}
                    />
                  </p>
                </div>
              </div>
            ) : (
              <div className="absolute inset-0 z-30 bg-white dark:bg-gray-900 flex items-center justify-center flex-col gap-4">
                <div className="animate-spin w-16 h-16 rounded-full border-4 border-gray-200 dark:border-gray-700 border-t-purple-600 dark:border-t-purple-600" />
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  <Translate id={modelViewersKeys.generatingLifestyle} />
                </p>
              </div>
            ))}
        </CheckPermissions>

        {/* Left */}
        <div className="absolute left-0 inset-y-0 z-10">
          {multiplyItems && (
            <PureIconButtonHelper
              className="opacity-0 hover:opacity-100 h-full p-10 lg:p-20 bg-black/30 focus:outline-none hidden md:block"
              iconClassName="w-10 h-10"
              icon={IconsEnum.CHEVRON_LEFT}
              onClick={handlePrevItem}
            />
          )}
        </div>
        {/* Right */}
        <div className="absolute right-0 inset-y-0 z-10">
          {multiplyItems && (
            <PureIconButtonHelper
              className="opacity-0 hover:opacity-100 h-full p-10 lg:p-20 bg-black/30 focus:outline-none hidden md:block"
              iconClassName="w-10 h-10"
              icon={IconsEnum.CHEVRON_RIGHT}
              onClick={handleNextItem}
            />
          )}
        </div>
        {/* Left bottom */}
        <div className="absolute bottom-4 left-4 z-20">
          <div className="relative flex items-center justify-center gap-2">
            <ModelViewerHelperTooltipButton
              icon={IconsEnum.KEYBOARD_SOLID}
              className={getButtonClass(false)}
              iconClassName="h-6 w-6"
            />

            {toolbarDivider}

            <PureTooltipIconButtonHelper
              className={getToolButtonClass(!withRuler)}
              icon={IconsEnum.HAND_SOLID}
              iconClassName="h-6 w-6"
              tooltipI18nText={modelViewersKeys.toolHand}
              tooltipPlacement={TooltipPlacement.TOP}
              onClick={() => setWithRuler(false)}
            />

            <PureTooltipIconButtonHelper
              className={getToolButtonClass(withRuler)}
              icon={IconsEnum.MEASURING_TAPE_ALT_SOLID}
              iconClassName="h-6 w-6"
              tooltipI18nText={modelViewersKeys.toolMeasure}
              tooltipPlacement={TooltipPlacement.TOP}
              onClick={() => setWithRuler(true)}
            />

            {withAvailableAnimations && (
              <PureTooltipIconButtonHelper
                className={getButtonClass(false)}
                icon={
                  isPaused
                    ? IconsEnum.PLAY_ALT_OUTLINE
                    : IconsEnum.PAUSE_ALT_OUTLINE
                }
                iconClassName="h-6 w-6"
                tooltipI18nText={isPaused ? words.play : words.pause}
                tooltipPlacement={TooltipPlacement.TOP}
                onClick={toggleAnimationState}
              />
            )}

            {toolbarDivider}

            {!hideBackground && (
              <ModelViewerBackgroundButton
                backgroundMode={backgroundMode}
                customColor={customColor}
                withImageOption={hasImageBackground}
                onSelectPreset={setPresetBackground}
                onSelectCustom={handleOpenColorPicker}
                onSelectImage={handleOpenImageModal}
              />
            )}

            <ModelViewerDisplaySettingsButton
              buttonClassName={getButtonClass(false)}
              withDimensions={withDimensions}
              onToggleDimensions={toggleWithDimensions}
              withGridAxesHelper={withGridAxesHelper}
              onToggleGridAxes={toggleWithGridAxesHelper}
              isNeutralMode={isNeutralMode}
              onToggleNeutral={toggleNeutralMaterial}
              units={units}
              onSelectUnits={setUnits}
              meshEdgesMode={meshEdgesMode}
              onSelectMesh={setMeshEdgesMode}
              aspectRatio={aspectRatio}
              onSelectAspect={setAspectRatio}
              imageSize={imageSize}
              onSelectImageSize={setImageSize}
              showGridLines={showGridLines}
              onToggleGridLines={toggleGridLines}
            />

            {toolbarDivider}

            <PureTooltipIconButtonHelper
              className={getButtonClass(false)}
              icon={IconsEnum.CAMERA_SOLID}
              iconClassName="h-6 w-6"
              tooltipI18nText={modelViewersKeys.screenshot}
              tooltipPlacement={TooltipPlacement.TOP}
              disabled={!isModelLoaded}
              onClick={() =>
                screenshotRef.current?.captureScreenshot({
                  filename: activeItem?.name,
                  // In auto mode nothing constrains the viewer box, so the
                  // capture is cropped to the ratio the rest of the flow uses.
                  cropToAspectRatio: getScreenshotCropRatio(),
                  withBackground:
                    backgroundMode !== 'default'
                      ? {
                          color:
                            backgroundMode !== 'image'
                              ? backgroundCssValue
                              : undefined,
                          imageUrl:
                            backgroundMode === 'image'
                              ? backgroundImageUrl ?? undefined
                              : undefined
                        }
                      : undefined
                })
              }
            />

            {withDownloadButton && (
              <DownloadModelButton
                icon={IconsEnum.DOWNLOAD_SOLID}
                className={getButtonClass(false)}
                iconClassName="h-6 w-6"
                model={activeItem}
              />
            )}
          </div>
        </div>
        {/* Right bottom */}
        <div className="absolute bottom-4 right-4 z-10">
          <div className="relative flex items-center justify-center gap-2">
            {multiplyItems && (
              <PureIconButtonHelper
                className="md:hidden py-2 pl-2 pr-2 rounded-md inline-flex items-center whitespace-nowrap text-sm font-medium leading-6 focus:ring-base hover:text-gray-950 dark:hover:text-white hover:bg-gray-200 dark:hover:bg-gray-700 focus:ring-offset-0"
                iconClassName="h-6 w-6"
                icon={IconsEnum.CHEVRON_LEFT}
                onClick={handlePrevItem}
              />
            )}
            {multiplyItems && (
              <PureIconButtonHelper
                className="md:hidden py-2 pl-2 pr-2 rounded-md inline-flex items-center whitespace-nowrap text-sm font-medium leading-6 focus:ring-base hover:text-gray-950 dark:hover:text-white hover:bg-gray-200 dark:hover:bg-gray-700 focus:ring-offset-0"
                iconClassName="h-6 w-6"
                icon={IconsEnum.CHEVRON_RIGHT}
                onClick={handleNextItem}
              />
            )}
          </div>
        </div>

        {/* Action buttons column — Studio Render is always visible while the
            model is loaded; AI Imagery Tool only when AI mode is inactive */}
        {isModelLoaded && (
          <div className="absolute bottom-4 right-4 z-20 flex flex-col items-end gap-2">
            {generationModelId && (
              <CheckPermissions
                action={
                  ModelViewerPermissions.READ_MODEL_VIEWER_RENDER_GENERATOR
                }
              >
                <PureIconButtonHelper
                  icon={IconsEnum.PHOTOGRAPH_SOLID}
                  iconClassName="h-5 w-5"
                  i18nText={modelViewersKeys.studioRender}
                  i18nTextClassName="ml-1.5"
                  className="py-3 pl-3 pr-6 space-x-1 rounded-full inline-flex items-center whitespace-nowrap text-sm font-medium leading-5 focus:ring-base text-white bg-blue-500 hover:bg-blue-600 shadow-sm hover:shadow-md transition-colors duration-150 ease-out disabled:opacity-60 disabled:cursor-not-allowed"
                  disabled={isRequestingRender}
                  onClick={handleRequestRender}
                />
              </CheckPermissions>
            )}

            {!isAiModeActive && (
              <CheckPermissions
                action={
                  ModelViewerPermissions.READ_MODEL_VIEWER_LIFESTYLE_GENERATOR
                }
              >
                <PureIconButtonHelper
                  icon={IconsEnum.SPARKLES_SOLID}
                  iconClassName="h-4 w-4"
                  i18nText={modelViewersKeys.aiImageryTool}
                  i18nTextClassName="ml-1.5"
                  className="py-3 pl-3 pr-6 space-x-1 rounded-full inline-flex items-center whitespace-nowrap text-sm font-medium leading-5 focus:ring-base text-white bg-white/15 dark:bg-black/70 hover:bg-white/5 dark:hover:bg-black/25 border-cosmos animate-gradient-angle shadow-sm hover:shadow-md transition-colors duration-150 ease-out"
                  onClick={() => setIsAiModeActive(true)}
                />
              </CheckPermissions>
            )}
          </div>
        )}

        {/* Lifestyle prompt card — only when model is loaded and AI mode is active */}
        <CheckPermissions
          action={ModelViewerPermissions.READ_MODEL_VIEWER_LIFESTYLE_GENERATOR}
        >
          {isModelLoaded && isAiModeActive && (
            <ModelViewerLifestylePromptCard
              prompt={prompt}
              selectedCommand={selectedCommand}
              references={referenceFiles}
              allowMultipleReferences
              isUploadingReference={isUploadingReference}
              isGenerating={isGenerating}
              generateError={generateError}
              onPromptChange={setPrompt}
              onSelectCommand={setSelectedCommand}
              onClearCommand={clearSelectedCommand}
              onReferenceFileChange={handleReferenceFilesChange}
              onReferenceFileDrop={handleReferenceFileDrop}
              onRemoveReference={handleRemoveReference}
              onGenerate={handleGenerate}
              allowedCommands={allowedCommands}
              onMinimize={() => setIsAiModeActive(false)}
              onOpenConfigurator={
                vymirConfig
                  ? () => {
                      setIsAiModeActive(false);
                      vymir.open();
                    }
                  : undefined
              }
            />
          )}
        </CheckPermissions>

        {colorPickerOpen && (
          <ModelViewerColorPickerPopup
            isOpen={colorPickerOpen}
            initialColor={colorPickerPrevColor}
            onChangePreview={setCustomColor}
            onConfirm={handleConfirmColorPicker}
            onCancel={handleCancelColorPicker}
          />
        )}
        <ModelViewerImageBackgroundModal
          isOpen={imageModalOpen}
          backgroundImageUrl={backgroundImageUrl}
          sessionUrls={sessionImageUrls}
          onSelectImage={setBackgroundImage}
          onAddSessionUrl={addSessionImageUrl}
          onRemoveSessionUrl={removeSessionImageUrl}
          onClose={handleCloseImageModal}
        />
      </div>

      {/* Sidebar — only when generations exist */}
      <CheckPermissions
        actions={[
          ModelViewerPermissions.READ_MODEL_VIEWER_LIFESTYLE_GENERATOR,
          ModelViewerPermissions.READ_MODEL_VIEWER_RENDER_GENERATOR
        ]}
      >
        {mergedGenerations.length > 0 && isAiModeActive && (
          <ModelViewerLifestyleSidebar
            generations={mergedGenerations}
            activeViewId={activeViewId}
            modelName={title ?? activeItem?.name}
            onSelectView={setActiveViewId}
            onAdjust={handleAdjust}
            onRemoveLifestyle={handleRemoveGeneration}
            onStopLifestyle={handleStopGeneration}
            onRemoveRender={handleRemoveRenderGeneration}
            onStopRender={handleStopRenderGeneration}
          />
        )}
      </CheckPermissions>
    </div>
  );
}

export default memo(forwardRef(ModelViewerEditor));
